import type {NavLinkProps} from 'quickstart/components/layout/NavLink'
import type {ReactNode} from 'react'
import {semiJoin, semiSplit} from 'tizra'
import * as A from '../../base'

export interface InputProps {
  props: NavLinkProps
  update: (props: NavLinkProps) => void
}

export const MenuInput = ({props, update}: InputProps) => {
  if (props.type !== 'menu') {
    return <A.Box>ERROR</A.Box>
  }
  return (
    <input
      value={props.label || ''}
      placeholder="label"
      onChange={ev => update({...props, label: ev.target.value})}
      className="t-input" // for reskin, safe for classic
    />
  )
}

export const LinkInput = ({props, update}: InputProps) => {
  if (props.type && props.type !== 'link' && props.type !== 'linkOut') {
    return <A.Box>ERROR</A.Box>
  }
  return (
    <>
      <input
        value={`${props.label || ''}`}
        placeholder="label"
        onChange={ev => update({...props, label: ev.target.value})}
        className="t-input" // for reskin, safe for classic
      />
      <input
        value={props.href || ''}
        placeholder="href"
        onChange={ev => update({...props, href: ev.target.value})}
        className="t-input" // for reskin, safe for classic
      />
    </>
  )
}

export const SeparatorInput = () => (
  <>
    <hr style={{display: 'block', width: '80px', alignSelf: 'center'}} />
  </>
)

export const ExpandoInput = ({props, update}: InputProps) => {
  if (props.type !== 'expando') {
    return <A.Box>ERROR</A.Box>
  }
  return (
    <A.Column>
      <A.Row>
        <input
          value={props.adminTag || ''}
          placeholder="admin tag"
          onChange={ev => update({...props, adminTag: ev.target.value})}
          className="t-input" // for reskin, safe for classic
        />
        <input
          defaultValue={semiJoin(props.metaTypes || [])}
          placeholder="meta-types"
          onChange={ev =>
            update({...props, metaTypes: semiSplit(ev.target.value)})
          }
          className="t-input" // for reskin, safe for classic
        />
      </A.Row>
      <A.Row>
        <input
          value={props.sortProp || ''}
          placeholder="sort prop"
          onChange={ev => update({...props, sortProp: ev.target.value})}
          className="t-input" // for reskin, safe for classic
        />
        <Select
          value={props.sortDirection || 'ascending'}
          options={[{value: 'ascending'}, {value: 'descending'}]}
          onChange={v => update({...props, sortDirection: v as any})}
        />
      </A.Row>
    </A.Column>
  )
}

export const BookshelfInput = ({props, update}: InputProps) => {
  if (props.type !== 'bookshelf') {
    return <A.Box>ERROR</A.Box>
  }
  return (
    <A.Column>
      <A.Row>
        <input
          value={`${props.label || ''}`}
          placeholder="label"
          onChange={ev => update({...props, label: ev.target.value})}
          className="t-input" // for reskin, safe for classic
        />
      </A.Row>
      <A.Hint>
        Use AUTO for global bookshelf title configured in Features tab.
      </A.Hint>
    </A.Column>
  )
}

export const AccountInput = ({props, update}: InputProps) => {
  if (props.type !== 'account') {
    return <A.Box>ERROR</A.Box>
  }
  return (
    <A.Column>
      <A.Row>
        <input
          value={`${props.label || ''}`}
          placeholder="label"
          onChange={ev => update({...props, label: ev.target.value})}
          className="t-input" // for reskin, safe for classic
        />
      </A.Row>
    </A.Column>
  )
}

const Select = ({
  options,
  value,
  onChange,
  placeholder,
}: {
  options: Array<{value: string; label?: ReactNode; shortLabel?: ReactNode}>
  value: string
  onChange: (value: string) => void
  placeholder?: string
}) => {
  const found = options.find(o => o.value === value)
  return (
    <A.Dropdown
      options={options.map(o => ({
        label: (
          <A.Row>
            <A.Box opacity={o.value === value ? 1 : 0}>
              <A.Icon.Check />
            </A.Box>
            <A.Box>{o.label || o.value}</A.Box>
          </A.Row>
        ),
        ...o,
      }))}
      onChoose={onChange}
    >
      <A.Row alignItems="center">
        <A.Box>
          {(found && (found.shortLabel || found.label || found.value)) ||
            placeholder}
        </A.Box>
        <A.Icon.ChevronDown />
      </A.Row>
    </A.Dropdown>
  )
}

const icons = {
  account: <A.Icon.User />,
  bookshelf: <A.Icon.Bookshelf />,
  expando: <A.Icon.Cpu />,
  link: <A.Icon.Link />,
  linkOut: <A.Icon.LinkOut />,
  menu: <A.Icon.Menu />,
  separator: <A.Icon.Separator />,
}

export const Switcher = ({
  options: _options,
  value,
  onChange,
  onRemove,
}: {
  options: Exclude<NavLinkProps['type'], undefined>[]
  value: Exclude<NavLinkProps['type'], undefined>
  onChange: (value: NavLinkProps['type']) => void
  onRemove?: () => void
}) => {
  const options: Array<{value: string; label?: ReactNode}> = _options
    .filter(v => v !== value)
    .map(value => ({
      value,
      label: (
        <A.Row>
          {icons[value]}Switch to {value}
        </A.Row>
      ),
    }))
  if (onRemove) {
    if (options.length) {
      options.push({value: '---'})
    }
    options.push({
      value: 'remove',
      label: (
        <A.Row>
          <A.Icon.Trash />
          Remove this {value}
        </A.Row>
      ),
    })
  }
  const trigger = (
    <A.Row alignItems="center">
      {icons[value || 'link']}
      <A.Icon.ChevronDown />
    </A.Row>
  )
  // Rendered as the same control in an unavailable state when there is
  // nowhere left to switch to, rather than as a plain element with no
  // control appearance (tasks.md — 3.2).
  return (
    <A.Dropdown
      disabled={!options.length}
      options={options}
      onChoose={value =>
        value === 'remove' ? onRemove?.() : onChange(value as any)
      }
    >
      {trigger}
    </A.Dropdown>
  )
}
